import React, { useState } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../MyAppText';
import MyDropDownPicker, { ValueType } from '../../MyDropDownPicker';
import { DropDownPickerProps } from 'react-native-dropdown-picker';
import { Hoverable } from 'react-native-web-hover';
import TooltipWrapper from '../../TooltipWrapper';
import inputStylesheet from '../../../styles/inputStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DistributiveOmit<T, K extends keyof any> = T extends any
    ? Omit<T, K>
    : never;

type Props = DistributiveOmit<DropDownPickerProps<ValueType>, 'open' | 'setOpen'>
    & {
        label?: string;
        onChange?: () => void;
        labelFontSize?: number;
        labelStyle?: StyleProp<TextStyle>;
        dropdownLabelStyle?: StyleProp<TextStyle>;
        viewStyle?: StyleProp<TextStyle>;
        required?: boolean;
        semiBoldLabel?: boolean;
        showTooltip?: boolean;
        tooltipText?: string;
        onTooltipHovered?: (hovered: boolean) => void;
        isLoading?: boolean;
        hideLabel?: boolean;
    };

function FilterDropdown({ label, viewStyle, required, showTooltip, tooltipText, onTooltipHovered, hideLabel = false, ...props }: Props) {
    const { styles } = useStyles(stylesheet);
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    const [isOpen, setIsOpen] = useState(false);

    return (
        <View style={[defaultInputStyle.container, styles.inputContainer(props.zIndex), viewStyle]}>
            {label && !hideLabel ?
                <View style={[defaultInputStyle.labelContainer, { zIndex: (props.zIndex || 0) + 1 }]}>
                    <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                    {showTooltip && tooltipText && <Hoverable>
                        {({ hovered }) => (
                            <TooltipWrapper
                                hovered={hovered}
                                onTooltipHovered={onTooltipHovered}
                                tooltipText={tooltipText}
                            />
                        )}
                    </Hoverable>}
                </View>
                : null}
            <MyDropDownPicker
                {...props}
                searchable={props.searchable ?? true}
                open={isOpen}
                setOpen={setIsOpen}
                dropDownDirection={props.dropDownDirection ?? 'BOTTOM'}
            />
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: (zIndex: number | undefined) => ({
        zIndex,
        minWidth: 200,
    }),
    filterText: (fontSize: number, semiBoldLabel: boolean) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
        fontWeight: semiBoldLabel ? '600' : '400'
    }),
}));

export default FilterDropdown;
